import React, { useEffect } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { Activity, Lock, ShieldCheck } from 'lucide-react';

const TelemetryCard = ({ levelName, vciScore = 0, onClick, className = "" }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  
  const rotateX = useTransform(y, [-100, 100], [10, -10]);
  const rotateY = useTransform(x, [-100, 100], [-10, 10]);

  const handleMouseMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set(event.clientX - centerX);
    y.set(event.clientY - centerY);
  };

  const handleMouseLeave = () => {
    if (!window.DeviceOrientationEvent || !('ontouchstart' in window)) {
        x.set(0);
        y.set(0);
    }
  };

  useEffect(() => {
    const handleOrientation = (e) => {
      if (e.gamma === null || e.beta === null) return;
      const tiltX = e.gamma * 3; 
      const tiltY = (e.beta - 45) * 3;
      x.set(Math.max(-100, Math.min(100, tiltX)));
      y.set(Math.max(-100, Math.min(100, tiltY)));
    };

    if (window.DeviceOrientationEvent && 'ontouchstart' in window) {
       window.addEventListener("deviceorientation", handleOrientation);
    }
    return () => window.removeEventListener("deviceorientation", handleOrientation);
  }, [x, y]);

  return (
    <motion.div 
      className={`relative w-64 h-80 sm:w-72 sm:h-96 cursor-pointer perspective-1000 group ${className}`}
      onMouseMove={handleMouseMove} 
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      whileHover={{ scale: 1.02 }}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
    >
      {/* Card Background - Enterprise Dark Mode */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-950 rounded-2xl shadow-xl overflow-hidden border border-slate-700">
         <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, #475569 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
      </div>

      {/* 3D Content Layer - Data as Identity */}
      <motion.div 
        className="absolute inset-0 flex flex-col items-center justify-center" 
        style={{ transform: "translateZ(40px)" }}
      >
        <Activity size={48} className="text-emerald-400 mb-4 opacity-80 drop-shadow-[0_0_15px_rgba(52,211,153,0.5)]" />
        <h2 className="text-6xl sm:text-7xl font-black text-white font-mono tracking-tighter drop-shadow-lg">{vciScore}</h2>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">Verified VCI Score</p>
        
        <div className="mt-6 px-4 py-1.5 bg-black/40 border border-slate-600 rounded-full font-mono text-[10px] text-slate-300 flex items-center gap-1.5 shadow-inner">
          <Lock size={12} className="text-blue-400" /> ZERO-BIAS IDENTITY
        </div>
      </motion.div>

      {/* Floating Badge Label */}
      <motion.div 
        className="absolute bottom-4 left-4 right-4 text-center"
        style={{ transform: "translateZ(60px)" }}
      >
        <span className="inline-flex items-center gap-1.5 bg-black/60 backdrop-blur-md text-white px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider border border-slate-600 shadow-xl">
          <ShieldCheck size={14} className="text-emerald-400" /> {levelName || "Tier 1: Baseline"}
        </span>
      </motion.div>
    </motion.div>
  );
};

export default TelemetryCard;